import { LitElement, html, nothing } from 'lit';
import { currentLang, t } from '../i18n';
import { toggleDecisionsCollapse, toggleSubtasksCollapse } from '../auth';
import {
  formatDeadlineLabel,
  formatGoalDeadlineLabel,
  goalDeadlineDate,
  isGoalDeadlineProvisional,
  isGoalOverdue,
  isOverdue,
  itemMatchesDeadlineFilter,
  provisionalDeadlineCopy,
  subtaskDeadlineView,
} from '../helpers';
import {
  currentDeadlineFilter,
  currentStatusFilter,
  pendingSubtaskGoalIds,
  webExpandedFilterItems,
  collapsedAiResults,
  expandedSubtaskDescriptions,
  openRejectFeedbackIds,
} from '../state';
import { AI_ACTION_ICONS_WEB, AI_ACTION_LABELS_WEB } from '../goals-actions';
import { aiWorkDispatches, aiWorkKind } from '../ai-work-kind';
import { isGoalCompleting, isGoalToggling } from '../goals-actions';
import { sanitizeHttpUrl, sanitizeImageUrl } from '../url-sanitizer';
import { renderSourceIcon, resolveSourceIcon } from '../source-icons';
import { goalDodDisplayText } from '../goal-dod-display';
import type { GoalWithSubtasks, SubtaskWithResult, DecisionOption, DecisionMaterial, CalendarEvent, GoalEventSurface, GoalEventSurfaceItem, SemanticCompletionRead, GrmaWaitingReadback, ReviewChangeProposal } from '../types';
import type { GoalComponentActionDetail, SubtaskComponentActionDetail } from './shared';
import { defineZenComponent } from './register';
import { resolveDecisionResultLabel as resolveDecisionResultLabelShared } from '../decision-labels';
import { isDecisionSurfaceReady } from '../decision-surface';
import { classifyFastGoalOptimization, fastGoalCreationPreview, fastGoalCreationPreviewConditionsText, isScreenshotFirstRowProcessing } from '../fast-goal-optimization';
import { canonicalJudgmentChosenOptionKey, isCanonicalJudgmentSelectionPending, isReviewProposalPending } from '../goals-actions';
import { canonicalJudgmentEntries, hasCanonicalJudgmentPacket } from '../canonical-judgment';
import './canonical-judgment-panel';

type GoalEventRelation = 'primary' | 'related' | 'suggested' | 'tentative';

/** The label of an AI task's kind; a kind without one reads as 「AIの作業」. */
function aiWorkKindLabel(kind: string): string {
  const labels = AI_ACTION_LABELS_WEB as Record<string, { ja: string; en: string }>;
  return (labels[kind] ?? labels.ai_work)[currentLang === 'en' ? 'en' : 'ja'];
}

const REPLACED_VALUE_EN: Record<NonNullable<ReviewChangeProposal['replaced']>, string> = {
  date_time: 'date or time', place: 'place', method: 'method', person: 'person', other_value: 'details',
};

function reviewProposalQuestionEn(item: ReviewChangeProposal): string {
  if (item.kind === 'withdraw_condition') return `The completion criterion "${item.condition ?? ''}" seems no longer needed after the latest message. Remove it and its tasks?`;
  if (item.kind === 'add_condition') {
    const who = `${item.message?.from ?? 'The other side'} sent a request the current completion criteria do not cover.`;
    return item.draft ? `${who} Add "${item.draft}" to the goal?` : `${who} Add it to the goal?`;
  }
  if (item.kind === 'rewrite_condition') {
    const what = `The ${item.replaced ? REPLACED_VALUE_EN[item.replaced] : 'details'} in "${item.condition ?? ''}" seem to have changed in the latest message.`;
    return item.draft ? `${what} Rewrite it to "${item.draft}"?` : `${what} Rewrite the completion criterion?`;
  }
  return item.question;
}

function reviewProposalMessageTime(item: ReviewChangeProposal): string | null {
  const at = item.message?.at ? new Date(item.message.at) : null;
  if (!at || Number.isNaN(at.getTime())) return null;
  return at.toLocaleString(currentLang === 'en' ? 'en-US' : 'ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export type ReviewClosure = { reason: 'done' | 'not_needed'; from: string | null; sent_by_principal: boolean; basis?: 'condition_met' | 'time_passed' };

/** #2086 S6 stage A: a task the review closed from a message, with whose message it was. */
export function reviewClosure(st: SubtaskWithResult): ReviewClosure | null {
  const value = st.metadata?.review_closed;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const closure = value as Record<string, unknown>;
  if (closure.reason !== 'done' && closure.reason !== 'not_needed') return null;
  return {
    reason: closure.reason,
    from: typeof closure.from === 'string' && closure.from.trim() ? closure.from.trim() : null,
    sent_by_principal: closure.sent_by_principal === true,
    ...(closure.basis === 'condition_met' || closure.basis === 'time_passed' ? { basis: closure.basis } : {}),
  };
}

/** #2798: an open task the review read as done or not needed from a message that could not close it. */
export function reviewCloseProposal(st: SubtaskWithResult): ReviewClosure | null {
  const value = st.metadata?.review_close_proposed;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const proposal = value as Record<string, unknown>;
  if (proposal.reason !== 'done' && proposal.reason !== 'not_needed') return null;
  return {
    reason: proposal.reason,
    from: typeof proposal.from === 'string' && proposal.from.trim() ? proposal.from.trim() : null,
    sent_by_principal: false,
  };
}

/** The `events` row of the hold a release takes off the calendar (`calendar_event.release_hold`). */
export function releaseHoldEventId(st: SubtaskWithResult): string | null {
  const calendarEvent = st.metadata?.calendar_event;
  if (!calendarEvent || typeof calendarEvent !== 'object' || Array.isArray(calendarEvent)) return null;
  const release = (calendarEvent as Record<string, unknown>).release_hold;
  if (!release || typeof release !== 'object' || Array.isArray(release)) return null;
  const eventId = (release as Record<string, unknown>).event_id;
  return typeof eventId === 'string' && eventId ? eventId : null;
}

export function reviewCloseProposalNote(proposal: ReviewClosure): string {
  const done = proposal.reason === 'done';
  if (currentLang === 'en') {
    const source = proposal.from ? `${proposal.from}'s reply` : 'the latest reply';
    return done ? `From ${source}: mark this done?` : `From ${source}: mark this no longer needed?`;
  }
  const source = proposal.from ? `${proposal.from}さんの返信` : '最新の返信';
  return done ? `${source}から、完了にしますか` : `${source}から、不要にしますか`;
}

export function reviewClosureNote(closure: ReviewClosure): string {
  // #3692: closed with no message behind it.
  if (closure.basis === 'condition_met') return currentLang === 'en' ? 'No longer needed: its condition is met.' : '条件を満たしたので不要にしました';
  if (closure.basis === 'time_passed') return currentLang === 'en' ? 'No longer needed: its time has passed.' : '時間が過ぎたので不要にしました';
  const done = closure.reason === 'done';
  if (currentLang === 'en') {
    const source = closure.sent_by_principal ? 'your message' : closure.from ? `${closure.from}'s reply` : 'the latest reply';
    return done ? `Marked done from ${source}.` : `Marked no longer needed from ${source}.`;
  }
  const source = closure.sent_by_principal ? 'あなたの送ったメッセージ' : closure.from ? `${closure.from}さんの返信` : '最新の返信';
  return done ? `${source}から完了にしました` : `${source}から不要と判断しました`;
}

type WaitingOn = NonNullable<NonNullable<GoalWithSubtasks['simplified_action_intake']>['waiting_on']>;

export function waitingOnText(waiting: WaitingOn): string {
  if (currentLang !== 'en') return waiting.label;
  switch (waiting.who) {
    case 'principal': return 'Waiting on you';
    case 'counterparty': return waiting.name ? `Waiting on ${waiting.name}` : 'Waiting on them';
    case 'someone_else': return 'Waiting on someone else';
    case 'nobody': return 'Nothing is waiting';
  }
}

function historyTime(at: string): string | null {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(currentLang === 'en' ? 'en-US' : 'ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function preparedActionCompat(st: SubtaskWithResult): Record<string, unknown> | null {
  const value = st.metadata?.action_runtime_compat;
  return value && typeof value === 'object' && !Array.isArray(value)
    && (value as Record<string, unknown>).schema_version === 'prepared_action_compat_subtask.v1'
    ? value as Record<string, unknown>
    : null;
}

const EXECUTOR_META: Record<string, { icon: string; cls: string; ja: string; en: string }> = {
  human: { icon: 'person', cls: 'badge-human', ja: '自分', en: 'Me' },
  ai: { icon: 'smart_toy', cls: 'badge-ai', ja: 'AI', en: 'AI' },
  decision: { icon: 'gavel', cls: 'badge-decision', ja: '判断', en: 'Decide' },
};

function isDecisionHiddenByCurrentFilters(st: SubtaskWithResult): boolean {
  return !st.decision_result && !itemMatchesDeadlineFilter(st.deadline, currentDeadlineFilter);
}

function isSubtaskHiddenByCurrentFilters(st: SubtaskWithResult): boolean {
  if (st.status === 'invalidated') return true;
  if (currentStatusFilter === 'review') {
    return st.status !== 'review';
  }
  return st.status !== 'done' && !itemMatchesDeadlineFilter(st.deadline, currentDeadlineFilter);
}

export function isDecisionVisibleOnUserSurface(st: SubtaskWithResult): boolean {
  if (st.status === 'invalidated') return false;
  if (st.decision_result && st.status === 'done') return true;
  const surface = st.metadata?.judgment_surface;
  if (!surface || typeof surface !== 'object' || Array.isArray(surface)) return true;
  const judgmentSurface = surface as Record<string, unknown>;
  return judgmentSurface.readiness !== 'none';
}

function isDecisionPrepNeeded(st: SubtaskWithResult): boolean {
  if (isDecisionSurfaceReady(st)) return false;
  const surface = st.metadata?.judgment_surface;
  if (!surface || typeof surface !== 'object' || Array.isArray(surface)) return false;
  const judgmentSurface = surface as Record<string, unknown>;
  return judgmentSurface.readiness === 'prep_needed';
}

function extractMeetingInfo(event: GoalEventSurfaceItem | CalendarEvent) {
  if (!event) return null;
  const patterns = [
    { pattern: /https?:\/\/meet\.google\.com\/\S+/i, name: 'Google Meet' },
    { pattern: /https?:\/\/(?:[\w-]+\.)*zoom\.us\/\S+/i, name: 'Zoom' },
    { pattern: /https?:\/\/teams\.microsoft\.com\/\S+/i, name: 'Microsoft Teams' },
  ];
  for (const field of [event.location, event.description]) {
    if (!field) continue;
    for (const { pattern, name } of patterns) {
      const match = field.match(pattern);
      if (match) return { url: match[0], name };
    }
  }
  return null;
}

function hasExternalWaitReadback(goal: GoalWithSubtasks) {
  return (goal.grma_materialization?.waiting_readbacks?.length ?? 0) > 0
    || goal.grma_materialization?.skipped_cell_reasons?.some((reason) =>
      reason === 'external_wait_readback_not_materialized' || reason === 'external_wait_not_materializable'
    ) === true;
}

function canRetryCanonicalProducerFailure(goal: GoalWithSubtasks): boolean {
  const readback = goal.grma_materialization;
  return readback?.state === 'producer_failed' && readback.can_retry === true;
}

function grmaEmptyStateCopy(goal: GoalWithSubtasks) {
  if ((goal.grma_materialization?.waiting_readbacks?.length ?? 0) > 0) return null;
  if (hasExternalWaitReadback(goal)) {
    return {
      tone: 'info',
      icon: 'mark_email_unread',
      spin: false,
      message: currentLang === 'en'
        ? 'Waiting for the other party to reply.'
        : '相手からの返信を待っています。',
      retry: false,
    };
  }

  const semantic = goal.grma_materialization_semantic;
  if (semantic?.user_landing) {
    if (semantic.user_landing === 'complete') return null;
    if (semantic.user_landing === 'optimizing') {
      // A quiet label, not a spinner: the choice is already saved and shown,
      // and the tasks are swapped in when the rebuild lands.
      return {
        tone: 'pending',
        icon: 'auto_awesome',
        spin: false,
        message: currentLang === 'en' ? 'Optimizing' : '最適化中',
        retry: false,
      };
    }
    if (semantic.user_landing === 'empty') {
      return {
        tone: 'info',
        icon: 'info',
        spin: false,
        message: currentLang === 'en'
          ? 'No additional tasks are needed for this goal yet.'
          : '今のところ、このゴールに追加タスクはありません。',
        retry: false,
      };
    }
    if (semantic.user_landing === 'failed') {
      if (!goal.grma_materialization) return null;
      return {
        tone: 'error',
        icon: 'error',
        spin: false,
        message: currentLang === 'en'
          ? 'Could not organize the goal details. The goal itself was created.'
          : 'ゴールの詳細整理に失敗しました。ゴール自体は作成済みです。',
        retry: canRetryCanonicalProducerFailure(goal),
      };
    }
    return {
      tone: 'warning',
      icon: 'warning',
      spin: false,
      message: currentLang === 'en'
        ? 'Goal details are waiting for more information.'
        : 'ゴールの詳細整理に追加情報が必要です。',
      retry: false,
    };
  }

  const readback = goal.grma_materialization;
  if (!readback || readback.state === 'materialized') return null;

  if (readback.state === 'unsupported') {
    return {
      tone: 'info',
      icon: 'warning',
      message: currentLang === 'en'
        ? 'This goal needs information we cannot turn into tasks yet.'
        : 'このゴールには、まだタスク化できない情報があります。',
      retry: false,
    };
  }
  if (readback.state === 'blocked') {
    return {
      tone: 'error',
      icon: 'radio_button_unchecked',
      message: currentLang === 'en'
        ? 'Could not organize the goal details because required information is missing or outdated.'
        : '必要な情報が不足または古いため、ゴールの詳細を整理できませんでした。',
      retry: false,
    };
  }
  if (readback.state === 'no_work') {
    return {
      tone: 'info',
      icon: 'info',
      message: currentLang === 'en'
        ? 'No additional tasks are needed for this goal yet.'
        : '今のところ、このゴールに追加タスクはありません。',
      retry: false,
    };
  }
  if (readback.state === 'invalid') {
    return {
      tone: 'error',
      icon: 'error',
      message: currentLang === 'en'
        ? 'Could not read the organized goal details. The goal itself was created.'
        : '整理済みのゴール詳細を読み取れませんでした。ゴール自体は作成済みです。',
      retry: false,
    };
  }
  if (readback.state === 'producer_failed') {
    return {
      tone: 'error',
      icon: 'error',
      message: currentLang === 'en'
        ? 'Could not organize the goal details. The goal itself was created.'
        : 'ゴールの詳細整理に失敗しました。ゴール自体は作成済みです。',
      retry: canRetryCanonicalProducerFailure(goal),
    };
  }
  return null;
}

/** #2932: a wait that only repeats the goal's name adds nothing under it. */
export function restatesGoalTitle(text: string | null | undefined, title: string | null | undefined): boolean {
  const plain = (value: string | null | undefined) => String(value || '').replace(/[\s。．.「」]/g, '');
  return plain(text) === plain(title);
}

/**
 * #3222: the plan's waits say 「待っています」 only while the turn is not the
 * principal's. Once the goal says whose turn it is, that decides.
 */
function goalAwaitsOtherSide(goal: GoalWithSubtasks): boolean {
  const who = goal.simplified_action_intake?.waiting_on?.who;
  return !who || who === 'counterparty' || who === 'someone_else';
}

function renderWaitingReadbacks(goal: GoalWithSubtasks) {
  const waitingReadbacks: GrmaWaitingReadback[] = Array.isArray(goal.grma_materialization?.waiting_readbacks)
    ? goal.grma_materialization.waiting_readbacks
    : [];
  if (waitingReadbacks.length === 0 || !goalAwaitsOtherSide(goal)) return nothing;
  return html`
    <div class="subtask-generating grma-waiting-readbacks">
      <span class="mi mi-xs">hourglass_top</span>
      ${currentLang === 'en' ? 'Waiting for an external reply' : '外部からの返答を待っています'}
    </div>
    ${waitingReadbacks.map((waiting) => html`
      <div class="grma-waiting-readback" style="margin:4px 0 4px 22px;">
        ${restatesGoalTitle(waiting.waiting_summary, goal.title) ? nothing : html`<div>${waiting.waiting_summary}</div>`}
        ${waiting.expected_signal
          ? html`<div style="color:var(--text-muted);font-size:12px;"><span class="mi mi-xs">notifications</span> ${currentLang === 'en' ? 'Expected: ' : '待っている合図: '}${waiting.expected_signal}</div>`
          : nothing}
      </div>
    `)}
  `;
}

type GoalReviewUnavailable = NonNullable<NonNullable<GoalWithSubtasks['simplified_action_intake']>['review_unavailable']>;

/** #2361 / #2631: why the Goal is not moving, and what brings it back. */
export function goalReviewUnavailableMessage(notice: GoalReviewUnavailable): string {
  const en = currentLang === 'en';
  if (notice.reason === 'action_reflection_failed') {
    if (notice.recovery === 'retrying') {
      return en
        ? 'The AI reviewed this goal but could not update its tasks yet. It will try again automatically.'
        : 'AI はこのゴールを見直しましたが、タスクへの反映がまだできていません。自動でやり直します。';
    }
    return en
      ? 'The AI reviewed this goal but could not update its tasks. It will try again on the next update, or when you edit the goal.'
      : 'AI はこのゴールを見直しましたが、タスクに反映できませんでした。次の更新か、ゴールを編集したときにやり直します。';
  }
  return en
    ? 'The AI could not review this goal after recent updates. Please check its status yourself.'
    : '最近の更新のあと、AI がこのゴールを見直せませんでした。状況をご自身で確認してください。';
}

function fastGoalOptimizationBanner(goal: GoalWithSubtasks) {
  const state = goal.fast_goal_initial_optimization;
  const presentation = classifyFastGoalOptimization(state);
  if (presentation === 'pending') {
    return {
      tone: 'pending',
      icon: 'progress_activity',
      spin: true,
      message: fastGoalCreationPreview(goal)
        ? (currentLang === 'en' ? 'Goal is ready. Creating tasks' : 'ゴールができました。タスクを作成中')
        : (currentLang === 'en' ? 'Organizing details' : '詳細を整理中'),
    };
  }
  if (presentation === 'stale') {
    return {
      tone: 'warning',
      icon: 'warning',
      spin: false,
      message: currentLang === 'en'
        ? 'Detail organization paused and is waiting for automatic recovery.'
        : '詳細整理が中断され、自動回復を待っています。',
    };
  }
  if (presentation === 'failed') {
    if (!goal.grma_materialization || canRetryCanonicalProducerFailure(goal)) return null;
    return {
      tone: 'error',
      icon: 'error',
      spin: false,
      message: currentLang === 'en'
        ? 'Could not organize the goal details. You can still edit the goal manually.'
        : 'ゴールの詳細整理に失敗しました。ゴールは手動で編集できます。',
    };
  }
  if (presentation === 'degraded') {
    return {
      tone: 'warning',
      icon: 'warning',
      spin: false,
      message: currentLang === 'en'
        ? 'Goal details were organized with some limits.'
        : 'ゴールの詳細整理は一部制限つきで完了しました。',
    };
  }
  return null;
}

export function actionRuntimeRecoveryBanner(goal: GoalWithSubtasks) {
  const recoveries = goal.simplified_action_intake?.action_runtime_recovery ?? [];
  const priority = ['setup_required', 'blocked_scope', 'permanent_failure', 'projection_gap', 'retryable_failure'];
  const recovery = [...recoveries].sort((left, right) => {
    const leftPriority = priority.indexOf(left.status);
    const rightPriority = priority.indexOf(right.status);
    return (leftPriority < 0 ? priority.length : leftPriority)
      - (rightPriority < 0 ? priority.length : rightPriority);
  })[0];
  if (!recovery) return null;
  const statusLabel = currentLang === 'en'
    ? ({
        setup_required: 'Connection setup required',
        blocked_scope: 'Additional permission required',
        retryable_failure: 'AI work can be retried',
        projection_gap: 'AI work display needs resynchronization',
        permanent_failure: 'AI work needs attention',
      } as Record<string, string>)[recovery.status] || 'AI work needs attention'
    : ({
        setup_required: '接続設定が必要です',
        blocked_scope: '追加の権限が必要です',
        retryable_failure: 'AI作業を再試行できます',
        projection_gap: 'AI作業の表示を再同期する必要があります',
        permanent_failure: 'AI作業の確認が必要です',
      } as Record<string, string>)[recovery.status] || 'AI作業の確認が必要です';
  const recoveryLabel = currentLang === 'en'
    ? ({
        retry_source_producer: 'Recreate this work from its source.',
        retry_projection: 'Retry the compatibility projection.',
      } as Record<string, string>)[recovery.recovery_action]
    : ({
        retry_source_producer: '元データからAI作業を再作成してください。',
        retry_projection: '互換表示の再同期を実行してください。',
      } as Record<string, string>)[recovery.recovery_action];
  return {
    icon: recovery.status === 'setup_required' ? 'settings'
      : recovery.status === 'blocked_scope' ? 'lock'
        : recovery.retryable ? 'replay' : 'error',
    message: [statusLabel, recoveryLabel].filter(Boolean).join(' '),
  };
}

/**
 * Read the one server-derived #1886 surface. The card deliberately does not
 * infer semantic completion from lifecycle, subtasks, or legacy facts.
 */
export function semanticCompletionReadback(goal: GoalWithSubtasks): SemanticCompletionRead | null {
  const readback = goal.simplified_action_intake?.semantic_completion;
  return readback && typeof readback === 'object' ? readback : null;
}

/**
 * Keep unresolved diagnostics verbatim. The server bounds and orders this
 * closed vocabulary; the UI only removes malformed non-string entries.
 */
export function semanticCompletionIssueCodes(goal: GoalWithSubtasks): string[] {
  const readback = semanticCompletionReadback(goal);
  if (!readback || readback.state !== 'unresolved' || !Array.isArray(readback.issue_codes)) return [];
  return readback.issue_codes.filter((code) => typeof code === 'string');
}

// Keep these lists identical to the Chrome extension goal card.
// V1 registers a comparator for few coordinates, so most Goals read
// `target_alignment_unresolved` by design (docs/grma/missing-piece.md). It is
// not something the user can act on, so it alone shows no banner.
const SEMANTIC_COMPLETION_SILENT_CODES = new Set(['target_alignment_unresolved']);
// Codes that only mean "not evaluable yet". Any other or unknown code is shown
// as a failure.
const SEMANTIC_COMPLETION_PENDING_CODES = new Set([
  'stale_input',
  'post_binding_revision_not_ready',
  'settlement_pointer_missing',
  'binding_target_missing',
  'participation_dependency_unresolved',
]);

export type SemanticCompletionBanner = {
  tone: 'pending' | 'error';
  icon: 'radio_button_unchecked' | 'error';
  message: string;
  issueCodes: string[];
};

/**
 * Present the unresolved readback without exposing its internal codes. The
 * exact codes stay available to callers (and the DOM data attribute) for
 * diagnostics only.
 */
export function semanticCompletionBanner(goal: GoalWithSubtasks): SemanticCompletionBanner | null {
  const issueCodes = semanticCompletionIssueCodes(goal);
  const shownCodes = issueCodes.filter((code) => !SEMANTIC_COMPLETION_SILENT_CODES.has(code));
  if (shownCodes.length === 0) return null;
  if (shownCodes.every((code) => SEMANTIC_COMPLETION_PENDING_CODES.has(code))) {
    return {
      tone: 'pending',
      icon: 'radio_button_unchecked',
      message: currentLang === 'en'
        ? 'The latest status has not been confirmed yet, so completion checks and some task creation are on hold. You can keep using this goal.'
        : '最新の状況をまだ確認できていないため、完了判定と一部のタスク作成を保留しています。ゴールはそのまま使えます。',
      issueCodes,
    };
  }
  return {
    tone: 'error',
    icon: 'error',
    message: currentLang === 'en'
      ? 'Could not organize the goal status. You can keep using this goal.'
      : 'ゴールの状態をうまく整理できませんでした。ゴールはそのまま使えます。',
    issueCodes,
  };
}

export function aiResultReviewAction(isPreparedActionCompat: boolean) {
  if (isPreparedActionCompat) {
    return {
      icon: 'task_alt',
      label: currentLang === 'en' ? 'Mark review complete' : 'レビュー確認を完了',
    };
  }
  return { icon: 'thumb_up', label: String(t('approve')) };
}

/** The goal's read state: an unresolved completion check, or a review that could not run. */
export function goalReadNotices(goal: GoalWithSubtasks) {
  const semanticBanner = semanticCompletionBanner(goal);
  const reviewUnavailable = goal.simplified_action_intake?.review_unavailable ?? null;
  return html`
  ${semanticBanner
    ? html`<div class=${semanticBanner.tone === 'error' ? 'subtask-failed' : 'subtask-warning'} data-semantic-completion-state="unresolved" data-issue-codes=${semanticBanner.issueCodes.join(' ')}><span class="mi mi-xs">${semanticBanner.icon}</span> ${semanticBanner.message}</div>`
    : nothing}
  ${reviewUnavailable
    ? html`<div class="subtask-failed" data-goal-review-unavailable=${reviewUnavailable.reason} data-goal-review-recovery=${reviewUnavailable.recovery ?? 'waiting_for_principal'}><span class="mi mi-xs">error</span> ${goalReviewUnavailableMessage(reviewUnavailable)}</div>`
    : nothing}
  `;
}

/**
 * How far organizing the goal has got: recovery, optimization, task
 * generation, waits read back, and the empty or failed states with a retry.
 */
export function goalWorkNotices(goal: GoalWithSubtasks, onRetry: (event: Event) => void) {
  const subtasks = (goal.subtasks || []).filter((st) => st.status !== 'invalidated');
  const recoveryBanner = actionRuntimeRecoveryBanner(goal);
  const semanticBanner = semanticCompletionBanner(goal);
  const optimizationBanner = fastGoalOptimizationBanner(goal);
  // The semantic banner already explains the limited result; keep the
  // degraded banner out of view but still use it for the gates below.
  const hideDegradedOptimizationBanner = Boolean(semanticBanner)
    && classifyFastGoalOptimization(goal.fast_goal_initial_optimization) === 'degraded';
  const grmaEmptyState = !recoveryBanner && !optimizationBanner && subtasks.length === 0 && (goal.decisions || []).length === 0
    ? grmaEmptyStateCopy(goal)
    : null;
  const waitingReadbackSurface = renderWaitingReadbacks(goal);
  const grmaToneClass = grmaEmptyState?.tone === 'error' ? 'subtask-failed' : 'subtask-generating';
  return html`
  ${recoveryBanner
    ? html`<div class="subtask-failed"><span class="mi mi-xs">${recoveryBanner.icon}</span> ${recoveryBanner.message}</div>`
    : nothing}
  ${optimizationBanner && !hideDegradedOptimizationBanner
    ? html`<div class=${optimizationBanner.tone === 'error' ? 'subtask-failed' : optimizationBanner.tone === 'warning' ? 'subtask-warning' : 'subtask-generating'}><span class=${`mi mi-xs${optimizationBanner.spin ? ' spin' : ''}`}>${optimizationBanner.icon}</span> ${optimizationBanner.message}</div>`
    : nothing}
  ${!recoveryBanner && !optimizationBanner && pendingSubtaskGoalIds.has(goal.id) ? html`<div class="subtask-generating"><span class="mi mi-xs spin">progress_activity</span> ${String(t('subtaskGenerating'))}</div>` : nothing}
  ${waitingReadbackSurface}
  ${grmaEmptyState
    ? html`<div class=${grmaToneClass}><span class=${`mi mi-xs${grmaEmptyState.spin ? ' spin' : ''}`}>${grmaEmptyState.icon}</span> ${grmaEmptyState.message}${grmaEmptyState.retry ? html` <button class="btn btn-sm btn-danger" style="margin-left:8px;padding:2px 8px;font-size:12px;" @click=${onRetry}><span class="mi mi-xs">replay</span> ${currentLang === 'en' ? 'Retry' : '再試行'}</button>` : nothing}</div>`
    : nothing}
  ${!optimizationBanner && !grmaEmptyState && !goal.grma_materialization && goal.subtask_generation_status === 'failed' && subtasks.length === 0 && (goal.decisions || []).length === 0
    ? html`<div class="subtask-failed"><span class="mi mi-xs">error</span> ${currentLang === 'en' ? 'Could not organize the goal details. The goal itself was created.' : 'ゴールの詳細整理に失敗しました。ゴール自体は作成済みです。'} <button class="btn btn-sm btn-danger" style="margin-left:8px;padding:2px 8px;font-size:12px;" @click=${onRetry}><span class="mi mi-xs">replay</span> ${currentLang === 'en' ? 'Retry' : '再試行'}</button></div>`
    : nothing}
  `;
}

// #2086: one change the review proposed after a new message. Nothing
// changes until the principal answers; withdrawing a condition, adding or
// rewriting one with the drafted sentence (stage B), and keeping things as
// they are are answered here.
export function reviewProposalTemplate(goalId: string, item: ReviewChangeProposal, host: EventTarget) {
  const pending = isReviewProposalPending(goalId);
  const answer = (event: Event, value: 'keep' | 'withdraw' | 'add' | 'rewrite') => {
    event.stopPropagation();
    host.dispatchEvent(new CustomEvent('review-proposal-answer', {
      bubbles: true, composed: true,
      detail: { goalId, proposalRef: item.proposal_ref, answer: value, conditionRef: item.condition_ref, draft: item.draft },
    }));
  };
  const messageTime = reviewProposalMessageTime(item);
  const hint = item.draft ? t('reviewProposalDraftHint')
    : item.kind === 'add_condition' ? t('reviewProposalAddHint')
    : item.kind === 'rewrite_condition' ? t('reviewProposalRewriteHint') : null;
  return html`<div class="subtask-warning condition-proposal" data-review-proposal=${item.kind}>
    <span class="mi mi-xs">radio_button_unchecked</span>
    <span class="condition-proposal-text">${currentLang === 'en' ? reviewProposalQuestionEn(item) : item.question}
      ${messageTime ? html`<span class="condition-proposal-hint">${currentLang === 'en' ? `Message of ${messageTime}` : `${messageTime} のご連絡`}</span>` : nothing}
      ${hint ? html`<span class="condition-proposal-hint">${String(hint)}</span>` : nothing}</span>
    ${item.kind === 'withdraw_condition' && item.condition_ref
      ? html`<button class="btn btn-sm btn-outline" ?disabled=${pending}
          @click=${(event: Event) => answer(event, 'withdraw')}><span class="mi mi-xs">remove_circle</span> ${String(t('reviewProposalWithdraw'))}</button>`
      : nothing}
    ${item.kind === 'add_condition' && item.draft
      ? html`<button class="btn btn-sm btn-outline" ?disabled=${pending}
          @click=${(event: Event) => answer(event, 'add')}><span class="mi mi-xs">add_circle</span> ${String(t('reviewProposalAdd'))}</button>`
      : nothing}
    ${item.kind === 'rewrite_condition' && item.draft && item.condition_ref
      ? html`<button class="btn btn-sm btn-outline" ?disabled=${pending}
          @click=${(event: Event) => answer(event, 'rewrite')}><span class="mi mi-xs">edit</span> ${String(t('reviewProposalRewrite'))}</button>`
      : nothing}
    <button class="btn btn-sm btn-outline" ?disabled=${pending}
      @click=${(event: Event) => answer(event, 'keep')}><span class="mi mi-xs">check</span> ${String(t('reviewProposalKeep'))}</button>
  </div>`;
}

export class ZenGoalCard extends LitElement {
  static properties = {
    goal: { attribute: false },
  };

  declare goal: GoalWithSubtasks | null;

  constructor() {
    super();
    this.goal = null;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.display = 'contents';
  }

  private dispatchGoalEvent(name: string, detail: GoalComponentActionDetail) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
  }

  private dispatchSubtaskEvent(name: string, detail: SubtaskComponentActionDetail) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
  }

  private goalDetail(extra: Partial<GoalComponentActionDetail> = {}): GoalComponentActionDetail {
    return {
      goalId: this.goal?.id || '',
      ...extra,
    };
  }

  private subtaskDetail(subtaskId: string, extra: Partial<SubtaskComponentActionDetail> = {}): SubtaskComponentActionDetail {
    return {
      goalId: this.goal?.id || '',
      subtaskId,
      ...extra,
    };
  }

  private onKeyboardActivate(handler: (event: Event) => void) {
    return (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handler(event);
    };
  }

  private onGoalDoneClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const isDone = element?.dataset.isDone === 'true';
    this.dispatchGoalEvent('goal-toggle-done', this.goalDetail({ value: isDone, element, event }));
  };

  private onGoalTitleClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-edit-title', this.goalDetail({ element, event }));
  };

  private onGoalDodClick = (event: Event) => {
    const section = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (!section || section.querySelector('textarea.goal-dod-input')) return;
    const element = section.querySelector('.goal-dod-text') as HTMLElement | null;
    if (!element) return;
    this.dispatchGoalEvent('goal-edit-dod', this.goalDetail({ element, event }));
  };

  private onAskAiClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-ask-ai', this.goalDetail({ title: this.goal?.title || '', element, event }));
  };

  private onGoalDeleteClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-delete', this.goalDetail({ title: this.goal?.title || '', element, event }));
  };

  private onGoalDeadlineClick = (event: Event) => {
    event.stopPropagation();
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-edit-deadline', this.goalDetail({ currentISO: element?.dataset.currentIso || '', element, event }));
  };

  private onToggleDecisionsClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (element) toggleDecisionsCollapse(element);
    this.dispatchGoalEvent('goal-toggle-decisions', this.goalDetail({ element, event }));
  };

  private onToggleSubtasksClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (element) toggleSubtasksCollapse(element);
    this.dispatchGoalEvent('goal-toggle-subtasks', this.goalDetail({ element, event }));
  };

  private onFilterToggleClick = (section: string) => (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-filter-toggle', this.goalDetail({ section, element, event }));
  };


  private onRetryGoalClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-retry-subtask-generation', this.goalDetail({ element, event }));
  };

  private onAddSubtaskClick = (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-add-subtask', this.goalDetail({ element, event }));
  };

  private onAddSubtaskKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    event.preventDefault();
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchGoalEvent('goal-add-subtask', this.goalDetail({ element, event }));
  };

  private onExecutorBadgeClick = (st: SubtaskWithResult) => (event: Event) => {
    const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.dispatchSubtaskEvent('subtask-cycle-executor', this.subtaskDetail(st.id, { element, event }));
  };

  private renderGoalDeadline() {
    if (!this.goal) return nothing;
    const goal = this.goal;
    const deadlineISO = (goalDeadlineDate(goal) ?? '').slice(0, 10);
    const goalOverdue = isGoalOverdue(goal);
    const provisional = isGoalDeadlineProvisional(goal) ? provisionalDeadlineCopy() : null;

    return html`
      ${goal.deadline
        ? html`
            <span
              class=${`goal-header-deadline deadline-editable${goalOverdue ? ' overdue' : ''}`}
              data-current-iso=${deadlineISO}
              title=${provisional ? `${provisional.title} ${String(t('setDeadline'))}` : String(t('setDeadline'))}
              role="button"
              tabindex="0"
              @click=${this.onGoalDeadlineClick}
              @keydown=${this.onKeyboardActivate(this.onGoalDeadlineClick)}
            >
              ${goalOverdue ? html`<span class="mi mi-xs">warning</span>` : nothing}
              ${provisional ? html`<span class="deadline-provisional" aria-label=${provisional.aria}>${provisional.tag}</span>` : nothing}
              ${formatGoalDeadlineLabel(goal)}
            </span>
          `
        : html`
            <span
              class="goal-header-deadline deadline-editable"
              data-current-iso=""
              title=${String(t('setDeadline'))}
              style="opacity:0.5;border:1px dashed var(--border);"
              role="button"
              tabindex="0"
              @click=${this.onGoalDeadlineClick}
              @keydown=${this.onKeyboardActivate(this.onGoalDeadlineClick)}
            >
              <span class="mi mi-xs">event</span>
              ${String(t('noDeadline'))}
            </span>
          `}
    `;
  }

  private renderGoalSourceLink() {
    const sourceUrl = sanitizeHttpUrl(this.goal?.source_url);
    if (!sourceUrl) return nothing;
    const sourceKind = this.goal?.source_type || this.goal?.created_from;
    const icon = resolveSourceIcon(sourceUrl, sourceKind);
    return html`<a class="goal-source-link" href=${sourceUrl} target="_blank" rel="noopener" title=${sourceUrl} aria-label=${icon.label}>${renderSourceIcon(sourceUrl, sourceKind, 'sm')}</a>`;
  }

  private renderTotalTime() {
    if (!this.goal) return nothing;
    const allItems = [...(this.goal.decisions || []), ...(this.goal.subtasks || [])];
    const totalMinutes = allItems.reduce((sum, st) => sum + (st.estimated_minutes || 0), 0);
    if (totalMinutes <= 0) return html`<span class="goal-time"></span>`;
    const label = totalMinutes >= 60
      ? Math.floor(totalMinutes / 60) + (currentLang === 'en' ? 'h' : '時間') + (totalMinutes % 60 > 0 ? totalMinutes % 60 + (currentLang === 'en' ? 'm' : '分') : '')
      : totalMinutes + (currentLang === 'en' ? 'm' : '分');
    return html`<span class="goal-time"><span class="mi mi-xs">schedule</span> ${label}</span>`;
  }

  private renderDecisionMaterials(materials: DecisionMaterial[]) {
    if (materials.length === 0) return nothing;
    return html`
      <div class="decision-materials">
        <div class="decision-materials-title"><span class="mi mi-xs">description</span> 判断材料</div>
        ${materials.map((m) => html`<div class="decision-material-item"><span class="decision-material-label">${m.title}:</span> ${m.content}</div>`)}
      </div>
    `;
  }

  private renderDecisionOptions(subtaskId: string, options: DecisionOption[], canDecide: boolean) {
    if (options.length === 0) return nothing;
    return html`
      <div class="decision-options">
        ${options.map((opt) => html`
          <button
            class=${`decision-option-btn${opt.recommended ? ' recommended' : ''}`}
            ?disabled=${!canDecide}
            @click=${(event: Event) => {
              if (!canDecide) return;
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-make-decision', this.subtaskDetail(subtaskId, {
                value: opt.value || opt.label,
                label: opt.label,
                element,
                event,
              }));
            }}
          >
            <span class="decision-option-label">${opt.label}</span>
            ${opt.continuation_preview?.title ? html`
              <span class="decision-continuation-preview"><span class="mi mi-xs">arrow_forward</span> ${t('decisionNext')}: ${opt.continuation_preview.title}</span>
            ` : nothing}
          </button>
        `)}
      </div>
    `;
  }

  private resolveDecisionResultLabel(st: SubtaskWithResult) {
    return resolveDecisionResultLabelShared(st as any, currentLang === 'en' ? 'en' : 'ja');
  }

  private renderDecisionItem(st: SubtaskWithResult, isExpanded: boolean) {
    if (st.decision_result && st.status === 'done') {
      return html`
        <div class="subtask-item subtask-done">
          <span class="decision-check decided"><span class="mi mi-xs">check_circle</span></span>
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:6px;">
              <span class="subtask-title" style="flex:1;">${st.title}</span>
              <span class="decision-resolved"><span class="mi mi-xs">arrow_forward</span> ${this.resolveDecisionResultLabel(st)}</span>
              <button class="subtask-action-btn" title="判断をやり直す" aria-label="判断をやり直す" style="color:var(--text-muted)" @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-reset-decision', this.subtaskDetail(st.id, { element, event }));
              }}><span class="mi mi-sm">undo</span></button>
              <button class="subtask-action-btn subtask-delete" title="削除" aria-label="削除" @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-delete', this.subtaskDetail(st.id, { title: st.title, element, event }));
              }}><span class="mi mi-sm">delete</span></button>
            </div>
          </div>
        </div>
      `;
    }

    const filterHidden = isDecisionHiddenByCurrentFilters(st);
    const hiddenStyle = filterHidden && !isExpanded ? 'display:none' : nothing;
    const shown = subtaskDeadlineView(st, this.goal?.deadline);
    const deadlineISO = shown.deadline ? (shown.deadline.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || '') : '';
    const overdue = !!(shown.deadline && isOverdue(shown.deadline));
    const canDecide = isDecisionSurfaceReady(st);
    const prepNeeded = isDecisionPrepNeeded(st);

    return html`
      <div class="subtask-item" id=${`subtask_${st.id}`} data-filter-hidden=${filterHidden ? 'true' : nothing} style=${hiddenStyle}>
        <span class="decision-check pending"><span class="mi mi-sm">radio_button_unchecked</span></span>
        <div style="flex:1;min-width:0;">
          <div style="display:flex;align-items:center;gap:6px;">
            <span class="subtask-title" style="flex:1;">${st.title}</span>
            ${prepNeeded ? html`<span class="decision-prep-badge"><span class="mi mi-xs">pending_actions</span> ${currentLang === 'en' ? 'Prep needed' : '準備待ち'}</span>` : nothing}
            <span
              class=${`subtask-deadline subtask-deadline-editable${overdue ? ' overdue' : ''}${shown.afterGoal ? ' after-goal' : ''}`}
              data-current-iso=${deadlineISO}
              title=${String(shown.afterGoal ? t('subtaskAfterGoalDeadline') : t('setDeadline'))}
              role="button"
              tabindex="0"
              style=${shown.deadline ? nothing : 'opacity:0.4;font-size:10px;'}
              @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-edit-deadline', this.subtaskDetail(st.id, { currentISO: element?.dataset.currentIso || '', element, event }));
              }}
              @keydown=${this.onKeyboardActivate((event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-edit-deadline', this.subtaskDetail(st.id, { currentISO: element?.dataset.currentIso || '', element, event }));
              })}
            >
              ${shown.deadline
                ? html`${overdue || shown.afterGoal ? html`<span class="mi mi-xs">warning</span>` : nothing}${formatDeadlineLabel(shown.deadline)}`
                : html`+ ${currentLang === 'en' ? 'due' : '期日'}`}
            </span>
            <button class="subtask-action-btn subtask-delete" title="削除" aria-label="削除" @click=${(event: Event) => {
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-delete', this.subtaskDetail(st.id, { title: st.title, element, event }));
            }}><span class="mi mi-sm">delete</span></button>
          </div>
          <div class="decision-panel">
            ${this.renderDecisionMaterials(st.decision_materials || [])}
            ${this.renderDecisionOptions(st.id, st.decision_options || [], canDecide)}
            ${canDecide ? html`<div class="decision-free-input">
              <input id=${`dfree_${st.id}`} placeholder="別の選択を入力..." @keydown=${(event: KeyboardEvent) => {
                if (event.key !== 'Enter' || event.isComposing) return;
                event.preventDefault();
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-submit-free-decision', this.subtaskDetail(st.id, { element, event }));
              }}>
              <button @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-submit-free-decision', this.subtaskDetail(st.id, { element, event }));
              }}>決定</button>
            </div>` : nothing}
          </div>
        </div>
      </div>
    `;
  }


  // #2086 S6 stage A: who the next step waits on, as the latest review read it.
  private renderWaitingOn(waiting: WaitingOn) {
    const mine = waiting.who === 'principal';
    const none = waiting.who === 'nobody';
    return html`<div class=${`waiting-on${mine ? ' waiting-on-mine' : none ? ' waiting-on-none' : ''}`} data-waiting-on=${waiting.who}>
      <span class="mi mi-xs">${mine ? 'radio_button_unchecked' : none ? 'check_circle' : 'hourglass_top'}</span>
      <span>${waitingOnText(waiting)}</span>
    </div>`;
  }

  // #2086 S6 stage A: one line per follow-up message (経緯), newest first.
  private renderFollowUpHistory() {
    const lines = this.goal?.simplified_action_intake?.follow_up_history ?? [];
    if (lines.length === 0) return nothing;
    return html`<details class="follow-up-history">
      <summary><span class="mi mi-xs">history</span> ${currentLang === 'en' ? 'History' : '経緯'} (${lines.length})</summary>
      <ul>
        ${[...lines].reverse().map((entry) => html`<li>
          <span class="follow-up-history-time">${historyTime(entry.at) ?? ''}</span>
          <span>${entry.line}</span>
        </li>`)}
      </ul>
    </details>`;
  }

  private renderDecisionsSection() {
    if (!this.goal) return nothing;
    // A trustworthy canonical packet is the sole choice authority for this
    // goal. Legacy decision_options remain available for goals without that
    // packet so older decision tasks keep working.
    if (hasCanonicalJudgmentPacket(this.goal.simplified_action_intake?.judgment_packet)) return nothing;
    const decisions = (this.goal.decisions || []).filter(isDecisionVisibleOnUserSurface);
    if (decisions.length === 0) return nothing;
    const decidedCount = decisions.filter((d) => d.decision_result && d.status === 'done').length;
    const hiddenCount = decisions.filter((st) => isDecisionHiddenByCurrentFilters(st)).length;
    const visibleCount = decisions.length - hiddenCount;
    const isExpanded = webExpandedFilterItems.has(`${this.goal.id}:d`);
    return html`
      <div class="decisions-section">
        <div class="decisions-title" role="button" tabindex="0" @click=${this.onToggleDecisionsClick} @keydown=${this.onKeyboardActivate(this.onToggleDecisionsClick)}>
          <span class="subtasks-toggle"><span class="mi mi-xs">expand_more</span></span>
          <span class="mi mi-xs">${decidedCount === decisions.length ? 'check_circle' : 'gavel'}</span>
          ${currentLang === 'en' ? 'Decisions' : '判断事項'} ${hiddenCount > 0 ? `(${visibleCount}/${decisions.length})` : `(${visibleCount})`}
        </div>
        <div class="decisions-body">
          ${decisions.map((st) => this.renderDecisionItem(st, isExpanded))}
          ${hiddenCount > 0 ? html`<button class="web-filter-toggle-btn" data-hidden-count=${String(hiddenCount)} @click=${this.onFilterToggleClick('d')}>${isExpanded ? '折りたたむ' : `+${hiddenCount}件`}</button>` : nothing}
        </div>
      </div>
    `;
  }

  private renderAiResult(st: SubtaskWithResult, isReview: boolean) {
    if (st.status === 'invalidated') return nothing;
    const aiResult = st.ai_result;
    const isPreparedActionCompat = !!preparedActionCompat(st);
    const reviewAction = aiResultReviewAction(isPreparedActionCompat);
    const isDispatchPending = !!(
      isReview
      && aiResult?.approved_at
      && !aiResult?.dispatched_at
      && aiWorkDispatches(st)
    );
    const calendarEvents = Array.isArray(aiResult?.result_metadata?.calendar_events)
      ? (aiResult?.result_metadata?.calendar_events as Array<Record<string, string>>)
      : [];
    const calendarLinks = calendarEvents.length > 0 ? html`
      <div class="calendar-links">
        ${calendarEvents.map((ev) => {
          const calendarUrl = sanitizeHttpUrl(ev.calendar_url);
          if (!calendarUrl) return nothing;
          return html`
          <a class="btn-calendar" href=${calendarUrl} target="_blank" rel="noopener" title="Googleカレンダーに追加">
            <span class="mi mi-xs">event</span>
            ${ev.title || ''}${ev.location ? ` @ ${ev.location}` : ''}
          </a>
        `;
        })}
      </div>
    ` : nothing;

    if (aiResult && aiResult.status === 'success' && aiResult.result_content) {
      const isCollapsed = collapsedAiResults.has(st.id);
      const showRejectFeedback = openRejectFeedbackIds.has(st.id);
      return html`
        <div class=${`ai-result-panel${isCollapsed ? ' collapsed' : ''}`} data-ai-panel-id=${st.id}>
          <div class="ai-result-header" role="button" tabindex="0" @click=${(event: Event) => {
            const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
            this.dispatchSubtaskEvent('ai-result-toggle', this.subtaskDetail(st.id, { element, event }));
          }} @keydown=${this.onKeyboardActivate((event: Event) => {
            const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
            this.dispatchSubtaskEvent('ai-result-toggle', this.subtaskDetail(st.id, { element, event }));
          })}>AI Result<span class="ai-result-toggle mi mi-xs">expand_less</span></div>
          <div style="position:relative;">
            <button class="btn-ai-copy-icon" title=${currentLang === 'en' ? 'Copy' : 'コピー'} aria-label=${currentLang === 'en' ? 'Copy AI result' : 'AI結果をコピー'} @click=${(event: Event) => {
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('ai-result-copy', this.subtaskDetail(st.id, { element, event }));
            }}><span class="mi mi-xs">content_copy</span></button>
            <textarea class="ai-result-content" data-subtask-id=${st.id} ?readonly=${isPreparedActionCompat}>${aiResult.result_content}</textarea>
          </div>
          ${calendarLinks}
          ${isDispatchPending ? html`
            <div class="ai-result-actions">
              <span class="ai-status ai-status-pending"><span class="mi mi-xs">schedule_send</span> ${currentLang === 'en' ? 'Approved, waiting for dispatch' : '承認済み・処理待ち'}</span>
            </div>
          ` : isReview ? html`
            <div class="ai-result-actions">
              <button class="btn-ai-approve" @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('ai-result-approve', this.subtaskDetail(st.id, { element, event }));
              }}><span class="mi mi-xs">${reviewAction.icon}</span> ${reviewAction.label}</button>
              ${isPreparedActionCompat ? nothing : html`<button class="btn-ai-reject" @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('ai-result-toggle-reject', this.subtaskDetail(st.id, { element, event }));
              }}><span class="mi mi-xs">thumb_down</span> ${String(t('retry'))}</button>`}
            </div>
            ${isPreparedActionCompat ? nothing : html`<div class="ai-reject-feedback" id=${`reject-feedback-${st.id}`} style=${showRejectFeedback ? nothing : 'display:none'}>
              <textarea id=${`reject-feedback-text-${st.id}`} rows="3" placeholder=${String(t('rejectFeedbackPlaceholder'))}></textarea>
              <p class="ai-reject-hint"><span class="mi mi-xs">info</span> ${currentLang === 'en' ? 'Claude will redo this task in your next sync.' : '次回の同期時にClaudeがやり直します。'}</p>
              <button class="btn-ai-reject-confirm" @click=${(event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('ai-result-reject', this.subtaskDetail(st.id, { element, event }));
              }}><span class="mi mi-xs">replay</span> ${String(t('rejectConfirm'))}</button>
            </div>`}
          ` : nothing}
        </div>
      `;
    }

    return isReview ? html`
      <div class="ai-result-missing">
        <span class="mi mi-xs">info</span>
        AI Result not recorded
        <button class="btn-ai-reject" style="margin-left:8px;" @click=${(event: Event) => {
          const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
          this.dispatchSubtaskEvent('ai-result-reject', this.subtaskDetail(st.id, { element, event }));
        }}><span class="mi mi-xs">replay</span> ${String(t('retry'))}</button>
      </div>
    ` : nothing;
  }

  private renderExecutorBadge(st: SubtaskWithResult) {
    const isDecision = st.task_type === 'decision';
    const displayKey = isDecision ? 'decision' : st.executor;
    const meta = EXECUTOR_META[displayKey] || EXECUTOR_META.human;
    const isClickable = !!(st.id && !preparedActionCompat(st) && !isDecision && st.status !== 'review' && st.status !== 'in_progress' && st.status !== 'invalidated');
    const tooltip = currentLang === 'en' ? 'Click to toggle' : 'クリックで切り替え';
    return html`
      <span
        class=${`badge ${meta.cls}${isClickable ? ' badge-executor-clickable' : ''}`}
        data-executor=${displayKey}
        data-executor-cycle-id=${isClickable ? st.id : nothing}
        title=${isClickable ? tooltip : nothing}
        role=${isClickable ? 'button' : nothing}
        tabindex=${isClickable ? '0' : nothing}
        @click=${isClickable ? this.onExecutorBadgeClick(st) : nothing}
        @keydown=${isClickable ? this.onKeyboardActivate(this.onExecutorBadgeClick(st)) : nothing}
      >
        <span class="mi mi-xs">${meta.icon}</span>
        ${currentLang === 'en' ? meta.en : meta.ja}
      </span>
    `;
  }

  private formatGoalEventDate(src: GoalEventSurfaceItem | CalendarEvent) {
    if (!src.start_at) return '';
    const start = new Date(src.start_at);
    const end = src.end_at ? new Date(src.end_at) : null;
    const isAllDay = !!src.is_all_day;
    if (isAllDay) {
      return start.toLocaleDateString(currentLang === 'en' ? 'en-US' : 'ja-JP', {
        month: 'numeric',
        day: 'numeric',
        weekday: 'short',
      }) + (currentLang === 'en' ? ' All day' : ' 終日');
    }
    const fmt = (d: Date) => d.toLocaleTimeString(currentLang === 'en' ? 'en-US' : 'ja-JP', { hour: '2-digit', minute: '2-digit' });
    let dateStr = start.toLocaleDateString(currentLang === 'en' ? 'en-US' : 'ja-JP', {
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
    }) + ` ${fmt(start)}`;
    if (end) dateStr += ` - ${fmt(end)}`;
    return dateStr;
  }

  private renderGoalEventRelationBadge(kind: GoalEventRelation) {
    const labels = {
      primary: { icon: 'check_circle', cls: 'success', label: currentLang === 'en' ? 'Primary Linked' : '主リンク' },
      related: { icon: 'link', cls: 'accent', label: currentLang === 'en' ? 'Related' : '関連' },
      suggested: { icon: 'radio_button_unchecked', cls: 'warning', label: currentLang === 'en' ? 'Suggested' : '候補' },
      tentative: { icon: 'schedule', cls: 'warning', label: currentLang === 'en' ? 'Tentative Hold' : '仮押さえ' },
    };
    const badge = labels[kind];
    return html`<span class=${`goal-event-relation-badge goal-event-relation-${badge.cls}`}><span class="mi mi-xs">${badge.icon}</span>${badge.label}</span>`;
  }

  private renderGoalEventStatusBadge(src: GoalEventSurfaceItem | CalendarEvent) {
    const statusMap: Record<string, { icon: string; cls: string; label: string }> = {
      confirmed: { icon: 'check_circle', cls: 'success', label: currentLang === 'en' ? 'Confirmed' : '確定' },
      tentative: { icon: 'schedule', cls: 'warning', label: currentLang === 'en' ? 'Tentative' : '仮' },
      cancelled: { icon: 'cancel', cls: 'danger', label: currentLang === 'en' ? 'Canceled' : 'キャンセル' },
    };
    const badge = statusMap[src.status ?? 'confirmed'] ?? statusMap.confirmed;
    return html`<span class=${`event-status-badge event-status-${badge.cls}`}><span class="mi mi-xs">${badge.icon}</span>${badge.label}</span>`;
  }

  private renderGoalEventTypeBadge(src: GoalEventSurfaceItem | CalendarEvent) {
    const typeMap: Record<string, string> = {
      meeting: currentLang === 'en' ? 'Meeting' : 'ミーティング',
      call: currentLang === 'en' ? 'Call' : '通話',
      deadline: currentLang === 'en' ? 'Deadline' : '締切',
      travel: currentLang === 'en' ? 'Travel' : '移動',
      meal: currentLang === 'en' ? 'Meal' : '食事',
      focus: currentLang === 'en' ? 'Focus' : '集中',
      personal: currentLang === 'en' ? 'Personal' : '個人',
      reminder: currentLang === 'en' ? 'Reminder' : 'リマインド',
      other: currentLang === 'en' ? 'Other' : 'その他',
    };
    const typeLabel = typeMap[src.event_type ?? 'other'] ?? '';
    return typeLabel ? html`<span class="event-type-badge">${typeLabel}</span>` : nothing;
  }

  private renderGoalEventCard(src: GoalEventSurfaceItem | CalendarEvent, relation: GoalEventRelation, goalId?: string) {
    const meetInfo = extractMeetingInfo(src);
    const meetUrl = sanitizeHttpUrl(meetInfo?.url);
    const dismissBtn = relation === 'tentative' && goalId
      ? html`<button class="goal-event-dismiss-btn" data-dismiss-hold-goal-id=${goalId} data-dismiss-hold-event-id=${src.id} data-dismiss-hold-event-title=${src.title ?? ''} title=${currentLang === 'en' ? 'Dismiss hold' : '仮押さえを解除'}><span class="mi mi-xs">close</span> ${currentLang === 'en' ? 'Dismiss' : '解除'}</button>`
      : nothing;
    return html`
      <div class="goal-event-card">
        <div class="goal-event-badges">
          ${this.renderGoalEventRelationBadge(relation)}
          ${this.renderGoalEventStatusBadge(src)}
          ${this.renderGoalEventTypeBadge(src)}
          ${dismissBtn}
        </div>
        ${src.title ? html`<div class="goal-event-title">${src.title}</div>` : nothing}
        <div class="goal-event-datetime">${this.formatGoalEventDate(src)}</div>
        ${meetInfo && meetUrl
          ? html`<a href=${meetUrl} target="_blank" rel="noopener" class="goal-event-meet-link">${currentLang === 'en' ? `Join ${meetInfo.name}` : `${meetInfo.name} に参加`} <span class="mi mi-xs">open_in_new</span></a>`
          : (src.location ? html`<span class="goal-event-location"><span class="mi mi-xs">location_on</span> ${src.location}</span>` : nothing)}
        ${src.description ? html`<div class="goal-event-desc">${src.description.slice(0, 60)}${src.description.length > 60 ? '…' : ''}</div>` : nothing}
      </div>
    `;
  }

  private renderGoalEventSection(surface?: GoalEventSurface | null, goalId?: string) {
    const cards: Array<{ src: GoalEventSurfaceItem | CalendarEvent; relation: GoalEventRelation }> = [];
    if (surface?.primary_linked) cards.push({ src: surface.primary_linked, relation: 'primary' });
    for (const item of surface?.related_events || []) cards.push({ src: item, relation: 'related' });
    for (const item of surface?.suggested_links || []) cards.push({ src: item, relation: 'suggested' });
    for (const item of surface?.tentative_holds || []) cards.push({ src: item, relation: 'tentative' });
    // #2977 stage 5: the event rows are the Goal's events; a time without a
    // row is shown in the header from the one reader (`goal.dates.event`).
    if (cards.length === 0) return nothing;
    return html`
      <div class="goal-event-label"><span class="mi mi-xs">event</span> ${currentLang === 'en' ? 'Event Links' : 'イベント連携'}</div>
      <div class="goal-event-body goal-event-surface">
        ${cards.map((card) => this.renderGoalEventCard(card.src, card.relation, goalId))}
      </div>
    `;
  }

  private renderAiActionLabel(st: SubtaskWithResult, isReview: boolean, isRunning: boolean, canExecute: boolean) {
    if (st.status === 'invalidated') return nothing;
    if (st.executor !== 'ai' || isReview || isRunning || canExecute) return nothing;
    const action = aiWorkKind(st);
    const icon = action ? (AI_ACTION_ICONS_WEB[action] || 'smart_toy') : 'help_outline';
    const label = action ? aiWorkKindLabel(action) : (currentLang === 'en' ? 'Not set' : '未設定');
    return html`<span class="ai-action-label"><span class="mi mi-xs">${icon}</span> ${label}</span>`;
  }

  private renderSubtaskStatus(st: SubtaskWithResult, isReview: boolean, isRunning: boolean, canExecute: boolean) {
    const aiResult = st.ai_result;
    if (st.status === 'invalidated') {
      const reason = st.invalidated_reason === 'parent_goal_deleted'
        ? (currentLang === 'en' ? 'Parent goal deleted' : '親ゴールが削除済み')
        : st.invalidated_reason === 'parent_goal_done'
          ? (currentLang === 'en' ? 'Parent goal completed' : '親ゴールが完了済み')
          : (currentLang === 'en' ? 'No longer actionable' : '実行対象外');
      return html`<span class="ai-status ai-status-invalidated"><span class="mi mi-xs">block</span> ${reason}</span>`;
    }
    const compat = preparedActionCompat(st);
    const compatState = typeof compat?.state === 'string' ? compat.state : null;
    if (compatState === 'setup_required') {
      return html`<span class="ai-status ai-status-invalidated"><span class="mi mi-xs">settings</span> ${currentLang === 'en' ? 'Setup required' : '接続設定が必要'}</span>`;
    }
    if (compatState === 'blocked_scope') {
      return html`<span class="ai-status ai-status-invalidated"><span class="mi mi-xs">lock</span> ${currentLang === 'en' ? 'Permission required' : '権限が必要'}</span>`;
    }
    if (compatState === 'projection_gap') {
      return html`<span class="ai-status ai-status-retry"><span class="mi mi-xs">sync_problem</span> ${currentLang === 'en' ? 'Sync retry required' : '同期の再試行が必要'}</span>`;
    }
    if (compatState === 'retryable_failure') {
      return html`<span class="ai-status ai-status-retry"><span class="mi mi-xs">replay</span> ${currentLang === 'en' ? 'Ready to retry' : '再試行できます'}</span>`;
    }
    if (compatState === 'permanent_failure') {
      return html`<span class="ai-status ai-status-invalidated"><span class="mi mi-xs">error</span> ${currentLang === 'en' ? 'Needs attention' : '確認が必要'}</span>`;
    }
    const isDispatchPending = !!(
      isReview
      && aiResult?.approved_at
      && !aiResult?.dispatched_at
      && aiWorkDispatches(st)
    );
    if (canExecute) {
      if (st.rejection_feedback) {
        return html`<span class="ai-status ai-status-retry"><span class="mi mi-xs">replay</span> ${currentLang === 'en' ? 'Redo requested' : 'やり直し待ち'} · ${st.rejection_feedback}</span>`;
      }
      const pendingAction = aiWorkKind(st);
      const pendingLabel = pendingAction ? aiWorkKindLabel(pendingAction) : '';
      return html`<span class="ai-status ai-status-pending"><span class="mi mi-xs">schedule</span> ${pendingLabel}${pendingLabel ? ' · ' : ''}${currentLang === 'en' ? 'Pending' : '処理待ち'}</span>`;
    }
    if (isRunning) {
      return html`<span class="ai-status ai-status-running"><span class="spinner spinner-sm"></span> ${String(t('executing'))}</span>`;
    }
    if (isDispatchPending) {
      return html`<span class="ai-status ai-status-pending"><span class="mi mi-xs">schedule_send</span> ${currentLang === 'en' ? 'Dispatch pending' : '処理待ち'}</span>`;
    }
    if (isReview) {
      return html`<span class="ai-status ai-status-waiting"><span class="mi mi-xs">rate_review</span> ${String(t('reviewing'))}</span>`;
    }
    return nothing;
  }

  private isTerminalSubtask(st: SubtaskWithResult) {
    return st.status === 'done' || st.status === 'invalidated';
  }

  private terminalSubtaskReason(st: SubtaskWithResult) {
    if (st.status === 'done') return currentLang === 'en' ? 'Completed' : '完了済み';
    if (st.invalidated_reason === 'parent_goal_deleted') {
      return currentLang === 'en' ? 'Parent goal deleted' : '親ゴールが削除済み';
    }
    if (st.invalidated_reason === 'parent_goal_done') {
      return currentLang === 'en' ? 'Parent goal completed' : '親ゴールが完了済み';
    }
    if (st.terminal_reason === 'parent_goal_closed') {
      return currentLang === 'en' ? 'Parent goal closed' : '親ゴールが終了済み';
    }
    if (st.terminal_reason === 'plan_changed') {
      return currentLang === 'en' ? 'Plan changed' : '計画変更済み';
    }
    if (st.terminal_reason === 'duplicate') {
      return currentLang === 'en' ? 'Duplicate' : '重複';
    }
    if (st.terminal_reason === 'manual') {
      return currentLang === 'en' ? 'Closed manually' : '手動で終了済み';
    }
    return currentLang === 'en' ? 'Superseded by current plan' : '現在の計画で置き換え済み';
  }

  private renderSubtaskItem(st: SubtaskWithResult, isExpanded: boolean) {
    if (st.status === 'invalidated') return nothing;
    const isDone = st.status === 'done';
    const isTerminal = this.isTerminalSubtask(st);
    const isReview = st.status === 'review';
    const isRunning = st.status === 'in_progress';
    const isAiTask = st.executor === 'ai' && aiWorkKind(st);
    const isPreparedActionCompat = !!preparedActionCompat(st);
    const canExecute = !!(isAiTask && st.status === 'pending');
    const shown = subtaskDeadlineView(st, this.goal?.deadline);
    const afterGoal = shown.afterGoal && !isTerminal;
    const deadlineISO = shown.deadline ? (shown.deadline.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || '') : '';
    const overdue = !!(shown.deadline && isOverdue(shown.deadline) && !isTerminal);
    const hidden = isSubtaskHiddenByCurrentFilters(st);
    const reanalysisMarker = st.source === 'reanalysis'
      ? html`<span class="reanalysis-marker"><span class="mi mi-xs">auto_awesome</span></span>`
      : nothing;
    const claudeUrl = sanitizeHttpUrl((st.description || '').match(/https:\/\/claude\.ai\/chat\/[a-zA-Z0-9\-]+/)?.[0]);
    const isAiLog = /^(AI相談の記録|AI consultation log|AIと相談|Consult with AI)$/.test(st.title);
    const isDescriptionExpanded = expandedSubtaskDescriptions.has(st.id);
    const closure = isDone ? reviewClosure(st) : null;
    const proposal = !isDone && !isTerminal ? reviewCloseProposal(st) : null;
    const releaseEventId = proposal ? releaseHoldEventId(st) : null;

    return html`
      <div class=${`subtask-item${isDone ? ' subtask-done' : ''}${isReview ? ' subtask-review' : ''}`} id=${`subtask_${st.id}`} data-filter-hidden=${hidden ? 'true' : nothing} style=${hidden && !isExpanded ? 'display:none' : nothing}>
        <button class=${`subtask-check${isDone ? ' checked' : ''}`} data-is-done=${String(isDone)} title=${isTerminal ? this.terminalSubtaskReason(st) : (currentLang === 'en' ? 'Mark done' : '完了にする')} aria-label=${isTerminal ? this.terminalSubtaskReason(st) : (currentLang === 'en' ? 'Mark task done' : 'タスクを完了にする')} ?disabled=${isTerminal || isPreparedActionCompat} @click=${(event: Event) => {
          if (isTerminal || isPreparedActionCompat) return;
          const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
          this.dispatchSubtaskEvent('subtask-toggle-done', this.subtaskDetail(st.id, { value: isDone, element, event }));
        }}>${isDone ? html`<span class="mi mi-xs">check</span>` : nothing}</button>
        <div style="flex:1;min-width:0;">
          <div class="subtask-row" style="display:flex;align-items:center;gap:6px;">
            ${isAiLog || isTerminal || isPreparedActionCompat
              ? html`<span class="subtask-title" style="flex:1;cursor:default;">${reanalysisMarker}${st.title}</span>`
              : html`<span class="subtask-title" style="flex:1;cursor:text;" title="クリックで編集" role="button" tabindex="0" @click=${(event: Event) => {
                  const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                  this.dispatchSubtaskEvent('subtask-edit-title', this.subtaskDetail(st.id, { element, event }));
                }} @keydown=${this.onKeyboardActivate((event: Event) => {
                  const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                  this.dispatchSubtaskEvent('subtask-edit-title', this.subtaskDetail(st.id, { element, event }));
                })}>${reanalysisMarker}${st.title}</span>`}
            ${claudeUrl
              ? html`<a href=${claudeUrl} target="_blank" rel="noopener" class="claude-convo-badge" title="Claude会話を開く"><span class="mi mi-xs">smart_toy</span><span class="mi mi-xs">open_in_new</span></a>`
              : (st.description ? html`<button class="subtask-desc-toggle" title="メモ" @click=${(event: Event) => {
                  const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                  this.dispatchSubtaskEvent('subtask-toggle-desc', this.subtaskDetail(st.id, { element, event }));
                }} aria-label="メモ"><span class="mi mi-xs">notes</span></button>` : nothing)}
            <span class="subtask-meta">
              ${this.renderExecutorBadge(st)}
              ${this.renderAiActionLabel(st, isReview, isRunning, canExecute)}
              <span class=${`subtask-deadline${isTerminal || isPreparedActionCompat ? '' : ' subtask-deadline-editable'}${overdue ? ' overdue' : ''}${afterGoal ? ' after-goal' : ''}`} data-current-iso=${deadlineISO} title=${isTerminal ? this.terminalSubtaskReason(st) : (afterGoal ? String(t('subtaskAfterGoalDeadline')) : (isPreparedActionCompat ? nothing : String(t('setDeadline'))))} role=${isTerminal || isPreparedActionCompat ? nothing : 'button'} tabindex=${isTerminal || isPreparedActionCompat ? nothing : '0'} style=${shown.deadline ? nothing : 'opacity:0.4;font-size:10px;'} @click=${(event: Event) => {
                if (isTerminal || isPreparedActionCompat) return;
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-edit-deadline', this.subtaskDetail(st.id, { currentISO: element?.dataset.currentIso || '', element, event }));
              }} @keydown=${isTerminal || isPreparedActionCompat ? nothing : this.onKeyboardActivate((event: Event) => {
                const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
                this.dispatchSubtaskEvent('subtask-edit-deadline', this.subtaskDetail(st.id, { currentISO: element?.dataset.currentIso || '', element, event }));
              })}>
                ${shown.deadline ? html`${overdue || afterGoal ? html`<span class="mi mi-xs">warning</span>` : nothing}${formatDeadlineLabel(shown.deadline)}` : html`+ ${currentLang === 'en' ? 'due' : '期日'}`}
              </span>
              <span class="subtask-time">${st.estimated_minutes ? html`<span class="mi mi-xs">schedule</span> ${st.estimated_minutes}${currentLang === 'en' ? 'm' : '分'}` : nothing}</span>
              <span class="subtask-status-slot">${this.renderSubtaskStatus(st, isReview, isRunning, canExecute)}</span>
            </span>
            ${isTerminal || isPreparedActionCompat ? nothing : html`<button class="subtask-action-btn subtask-delete" title="削除" aria-label="削除" @click=${(event: Event) => {
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-delete', this.subtaskDetail(st.id, { title: st.title, element, event }));
            }}><span class="mi mi-sm">delete</span></button>`}
          </div>
          ${claudeUrl || !st.description ? nothing : html`<div class=${`subtask-description${isDescriptionExpanded ? ' expanded' : ''}`} id=${`stDesc_${st.id}`}>${st.description}</div>`}
          ${closure ? html`<div class="review-closure-note" data-review-closure=${closure.reason}>
            <span class="mi mi-xs">mark_email_read</span>
            <span>${reviewClosureNote(closure)}</span>
            <button class="btn-link review-closure-undo" @click=${(event: Event) => {
              event.stopPropagation();
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-toggle-done', this.subtaskDetail(st.id, { value: true, element, event }));
            }}>${currentLang === 'en' ? 'Undo' : '戻す'}</button>
          </div>` : nothing}
          ${proposal && releaseEventId ? html`<div class="review-closure-note review-close-proposal" data-review-close-proposal=${proposal.reason}>
            <span class="mi mi-xs">radio_button_unchecked</span>
            <span>${reviewCloseProposalNote(proposal)}</span>
            <button class="btn-link review-closure-undo" data-dismiss-hold-goal-id=${this.goal.id} data-dismiss-hold-event-id=${releaseEventId}>${currentLang === 'en' ? 'Remove the hold' : '外す'}</button>
            <button class="btn-link review-closure-undo" @click=${(event: Event) => {
              event.stopPropagation();
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-toggle-done', this.subtaskDetail(st.id, { value: false, element, event }));
            }}>${currentLang === 'en' ? 'Keep the hold' : '残す'}</button>
          </div>` : proposal ? html`<div class="review-closure-note review-close-proposal" data-review-close-proposal=${proposal.reason}>
            <span class="mi mi-xs">radio_button_unchecked</span>
            <span>${reviewCloseProposalNote(proposal)}</span>
            <button class="btn-link review-closure-undo" @click=${(event: Event) => {
              event.stopPropagation();
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-toggle-done', this.subtaskDetail(st.id, { value: false, element, event }));
            }}>${currentLang === 'en' ? (proposal.reason === 'done' ? 'Mark done' : 'Mark not needed') : (proposal.reason === 'done' ? '完了にする' : '不要にする')}</button>
            <button class="btn-link review-closure-undo" @click=${(event: Event) => {
              event.stopPropagation();
              const element = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
              this.dispatchSubtaskEvent('subtask-decline-review-close', this.subtaskDetail(st.id, { element, event }));
            }}>${currentLang === 'en' ? 'Keep open' : 'まだ残す'}</button>
          </div>` : nothing}
          ${this.renderAiResult(st, isReview)}
        </div>
      </div>
    `;
  }

  private renderSubtasksSection() {
    if (!this.goal) return nothing;
    const goal = this.goal;
    const subtasks = (goal.subtasks || []).filter((st) => st.status !== 'invalidated');
    const hiddenCount = subtasks.filter((st) => isSubtaskHiddenByCurrentFilters(st)).length;
    const visibleCount = subtasks.length - hiddenCount;
    const isExpanded = webExpandedFilterItems.has(`${goal.id}:s`);
    const eventTime = goal.linked_event?.start_at ? new Date(goal.linked_event.start_at).getTime() : null;
    const getPhase = (s: SubtaskWithResult) => {
      if (eventTime && s.deadline) {
        return new Date(s.deadline).getTime() <= eventTime ? 'before' : 'after';
      }
      return s.piece_phase || null;
    };
    const beforeTasks = subtasks.filter((s) => getPhase(s) === 'before');
    const afterTasks = subtasks.filter((s) => getPhase(s) === 'after');
    const normalTasks = subtasks.filter((s) => !getPhase(s));
    const hasPhases = beforeTasks.length > 0 || afterTasks.length > 0;
    const reviewProposals = goal.simplified_action_intake?.review_change_proposals ?? [];
    const waitingOn = goal.status === 'active' ? goal.simplified_action_intake?.waiting_on ?? null : null;

    return html`
      <div class="subtasks">
        ${this.renderDecisionsSection()}
        <div class="subtasks-title-row">
          <div class="subtasks-title" role="button" tabindex="0" @click=${this.onToggleSubtasksClick} @keydown=${this.onKeyboardActivate(this.onToggleSubtasksClick)}>
            <span class="subtasks-toggle"><span class="mi mi-xs">expand_more</span></span>
            <span class="subtasks-label">${currentLang === 'en' ? 'Tasks' : 'タスク'} ${subtasks.length ? (hiddenCount > 0 ? `(${visibleCount}/${subtasks.length})` : `(${visibleCount})`) : ''}</span>
          </div>
        </div>
        <div class="subtasks-body">
          ${goalReadNotices(goal)}
          ${waitingOn ? this.renderWaitingOn(waitingOn) : nothing}
          ${reviewProposals.map((item) => reviewProposalTemplate(goal.id, item, this))}
          ${goalWorkNotices(goal, this.onRetryGoalClick)}
          ${hasPhases && beforeTasks.length > 0 ? html`<div class="phase-label"><span class="phase-dot phase-dot-before"></span>${currentLang === 'en' ? 'Preparation' : '準備'}</div>` : nothing}
          ${beforeTasks.map((st) => this.renderSubtaskItem(st, isExpanded))}
          ${normalTasks.map((st) => this.renderSubtaskItem(st, isExpanded))}
          ${hasPhases && afterTasks.length > 0 ? html`<div class="phase-label"><span class="phase-dot phase-dot-after"></span>${currentLang === 'en' ? 'Follow-up' : 'フォローアップ'}</div>` : nothing}
          ${afterTasks.map((st) => this.renderSubtaskItem(st, isExpanded))}
          ${hiddenCount > 0 ? html`<button class="web-filter-toggle-btn" data-hidden-count=${String(hiddenCount)} @click=${this.onFilterToggleClick('s')}>${isExpanded ? '折りたたむ' : `+${hiddenCount}件`}</button>` : nothing}
          ${this.renderFollowUpHistory()}
          <div class="subtask-add">
            <input class="subtask-add-input" id=${`addst_${goal.id}`} placeholder=${`${String(t('addSubtaskOrFeedback'))}...`} @keydown=${this.onAddSubtaskKeydown}>
            <button class="btn btn-sm btn-outline" @click=${this.onAddSubtaskClick}>${String(t('submit'))}</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCanonicalJudgmentPanel() {
    if (!this.goal) return nothing;
    // #2422 stage 2: one panel per judgment when the Goal declares several.
    const entries = canonicalJudgmentEntries(this.goal.simplified_action_intake);
    if (entries.length === 0) return nothing;
    return html`${entries.map((entry) => html`
      <zen-canonical-judgment-panel
        .goalId=${this.goal!.id}
        .packet=${entry.packet}
        .conditionRef=${entry.condition_ref}
        .axis=${entry.axis}
        .pending=${isCanonicalJudgmentSelectionPending(this.goal!.id)}
        .chosenOptionKey=${canonicalJudgmentChosenOptionKey(this.goal!.id, entry.packet?.selection_snapshot_token)}
      ></zen-canonical-judgment-panel>
    `)}`;
  }

  render() {
    if (!this.goal) return nothing;
    const goal = this.goal;
    const isDone = goal.status === 'done';
    const isToggling = isGoalToggling(goal.id);
    const isCompleting = isGoalCompleting(goal.id);
    // #2660: while the tasks are still being created, show the written title
    // and conditions instead of the provisional first row.
    const creationPreview = isDone ? null : fastGoalCreationPreview(goal);
    const displayTitle = creationPreview?.title ?? goal.title;
    const dodDisplayText = creationPreview
      ? fastGoalCreationPreviewConditionsText(creationPreview)
      : goalDodDisplayText(goal.dod, goal.goal_dod_items);
    const screenshotUrl = sanitizeImageUrl(goal.screenshot_url);
    const thumb = screenshotUrl
      ? html`<img class="goal-thumb" src=${screenshotUrl} alt="" data-lightbox-src=${screenshotUrl}>`
      : nothing;

    return html`
      <div class=${`goal-card fade-in${isDone ? ' goal-completed' : ''}${isCompleting ? ' just-completed' : ''}`} id=${`goal_${goal.id}`}>
        <div class="goal-header">
          <button class=${`goal-check${isDone ? ' checked' : ''}`} data-is-done=${String(isDone)} title=${isDone ? '未完了に戻す' : '完了にする'} aria-label=${isDone ? '未完了に戻す' : '完了にする'} ?disabled=${isToggling} @click=${this.onGoalDoneClick}>
          </button>
          ${thumb}
          <div class="goal-info">
            <div class="goal-title" style="cursor:text;" title="クリックで編集" role="button" tabindex="0" @click=${this.onGoalTitleClick} @keydown=${this.onKeyboardActivate(this.onGoalTitleClick)}>${displayTitle}</div>
            ${isScreenshotFirstRowProcessing(goal) ? html`<div class="goal-screenshot-processing-label"><span class="mi mi-xs">progress_activity</span>${currentLang === 'en' ? 'Creating a goal from the image…' : '画像からゴールを作成中…'}</div>` : nothing}
            <div class="goal-dod-section" role="button" tabindex="0" @click=${this.onGoalDodClick} @keydown=${this.onKeyboardActivate(this.onGoalDodClick)}>
              <div class="goal-dod-label"><span class="mi mi-xs">task_alt</span> ${String(t('dodLabel'))} <span class="mi mi-xs goal-dod-edit-icon">edit</span></div>
              ${dodDisplayText ? html`<div class="goal-dod-text" data-dod-current=${dodDisplayText}>${dodDisplayText}</div>` : html`<div class="goal-dod-text goal-dod-empty" data-dod-current="">${String(t('dodAdd'))}</div>`}
              ${goal.reconcile_status === 'drifted' ? html`<div class="goal-dod-drift-badge"><span class="mi mi-xs">rate_review</span> ${String(t('dodDriftMsg'))}</div>` : nothing}
            </div>
            ${goal.ai_summary ? html`<div class="goal-consultation-summary"><span class="mi mi-xs">chat_bubble</span> ${goal.ai_summary}</div>` : nothing}
          </div>
          <div class="goal-header-actions">
            <div class="goal-meta-group">
              ${this.renderGoalSourceLink()}
              ${this.renderGoalDeadline()}
              ${this.renderTotalTime()}
              <button class="btn btn-sm goal-ask-ai-btn" title=${currentLang === 'en' ? 'Ask AI' : 'AIに相談'} @click=${this.onAskAiClick}><span class="mi mi-sm">smart_toy</span> ${currentLang === 'en' ? 'Ask AI' : 'AI相談'}</button>
            </div>
            <div class="goal-action-group">
              <button class="btn btn-sm btn-danger goal-delete-btn" title="削除" aria-label="削除" @click=${this.onGoalDeleteClick}><span class="mi mi-sm">delete</span></button>
            </div>
          </div>
        </div>
        ${this.renderGoalEventSection(goal.goal_event_surface, goal.id)}
        ${this.renderCanonicalJudgmentPanel()}
        ${this.renderSubtasksSection()}
      </div>
    `;
  }
}

defineZenComponent('zen-goal-card', ZenGoalCard);
